import {NavCard, OneColumnGrid, Page} from '../lib/material-ui-components'
import {path} from '../lib/routes'
import {
  navigateTo3DExperience, 
  experienceConfigs, 
  getAllExperiencesFromWordPress, 
  searchExperiencesFromWordPress,
  testWordPressConnection,
  getWordPressConnectionStatus,
  clearWordPressCache,
  runWordPressDiagnostics
} from './experience-navigator'
import {getModelPath, testModelLoad} from './assets-config'
import {discoverModels, getAvailableModelsInfo, testAllConfiguredModels} from './model-discovery'
import {initializeRemoteModels, getModelsByCategory, addModelWithAutoConfig} from './remote-models-manager'
import {AuthSystem} from '../lib/auth-system'
import {showCurrentLoginUrl, copyLoginUrlToClipboard, showAuthInfo, testLoginUrl} from '../lib/login-url-generator'
import {getAllExperiences, generateExperienceLoginUrl, generateShareableExperienceUrl} from '../lib/experience-routes'
import {generateAllARExperiences} from './ar-experiences-config'

declare let React: any
declare let ReactRouterDOM: any

const {withRouter} = ReactRouterDOM

const Home = withRouter(({location, history}: any) => {
  const authSystem = AuthSystem.getInstance()
  
  // Stato per il filtro di ricerca
  const [searchTerm, setSearchTerm] = React.useState('')
  
  // Stato per le esperienze caricate da WordPress
  const [experiences, setExperiences] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  
  // Stato per la connessione WordPress
  const [wordpressStatus, setWordpressStatus] = React.useState({
    isOnline: false,
    lastCheck: null,
    health: null
  })
  
  // Carica le esperienze da WordPress al mount del componente
  React.useEffect(() => {
    const loadExperiences = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Test connessione WordPress
        const isOnline = await testWordPressConnection()
        setWordpressStatus(prev => ({
          ...prev,
          isOnline,
          lastCheck: new Date()
        }))
        
        if (isOnline) {
          console.log('✅ WordPress online, carico esperienze...')
          const wpExperiences = await getAllExperiencesFromWordPress()
          setExperiences(wpExperiences)
        } else {
          console.log('⚠️ WordPress offline, uso esperienze di fallback...')
          setExperiences(Object.values(experienceConfigs))
        }
      } catch (err) {
        console.error('❌ Errore nel caricamento esperienze:', err)
        setError('Errore nel caricamento delle esperienze')
        // Fallback alle esperienze hardcoded
        setExperiences(Object.values(experienceConfigs))
      } finally {
        setLoading(false)
      }
    }

    loadExperiences()
  }, [])

  // Aggiungi le esperienze AR generate dinamicamente
  const arExperiences = generateAllARExperiences()
  const allExperiences = [...experiences, ...arExperiences]

  // Filtra le esperienze in base al termine di ricerca
  const filteredExperiences = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return allExperiences
    }
    
    const term = searchTerm.toLowerCase()
    return allExperiences.filter(exp => 
      exp.title.toLowerCase().includes(term) ||
      (exp.text && exp.text.toLowerCase().includes(term)) ||
      exp.id.toLowerCase().includes(term) ||
      (exp.config && exp.config.description && exp.config.description.toLowerCase().includes(term))
    )
  }, [allExperiences, searchTerm])

  const handleModelClick = (config: any) => {
    navigateTo3DExperience(history, location, config)
  }

  const handleTestModels = async () => {
    console.log('🧪 Test modelli configurati...')
    await testAllConfiguredModels()
  }

  const handleDiscoverModels = async () => {
    console.log('🔍 Scoperta nuovi modelli...')
    await discoverModels()
  }

  const handleShowModelsInfo = () => {
    console.log('📋 Informazioni modelli...')
    getAvailableModelsInfo()
  }

  const handleInitializeRemoteModels = async () => {
    console.log('🚀 Inizializzazione modelli remoti...')
    await initializeRemoteModels()
  }

  const handleShowModelsByCategory = () => {
    console.log('📂 Modelli per categoria...')
    const categorized = getModelsByCategory()
    console.log('Categorie disponibili:', categorized)
  }

  const handleShowLoginUrl = () => {
    showCurrentLoginUrl()
  }

  const handleTestWordPressConnection = async () => {
    console.log('🧪 Test connessione WordPress...')
    const isOnline = await testWordPressConnection()
    setWordpressStatus(prev => ({
      ...prev,
      isOnline,
      lastCheck: new Date()
    }))
  }

  const handleClearWordPressCache = () => {
    console.log('🧹 Pulizia cache WordPress...')
    clearWordPressCache()
    // Ricarica le esperienze
    window.location.reload()
  }

  const handleWordPressDiagnostics = async () => {
    console.log('🔍 Avvio diagnostica WordPress...')
    const diagnostics = await runWordPressDiagnostics()
    
    if (diagnostics) {
      // Aggiorna lo stato con le informazioni diagnostiche
      setWordpressStatus(prev => ({
        ...prev,
        isOnline: diagnostics.isOnline,
        lastCheck: new Date()
      }))
      
      // Mostra risultati in console
      if (diagnostics.isOnline) {
        console.log('✅ Diagnostica completata: API funzionante')
      } else {
        console.log('❌ Diagnostica completata: API non disponibile')
        console.log('🔗 URL tentato:', diagnostics.baseUrl)
        console.log('🌐 Origin corrente:', diagnostics.currentOrigin)
        if (diagnostics.lastError) {
          console.log('🚨 Errore:', diagnostics.lastError)
        }
      }
    }
  }

  const handleCopyLoginUrl = async () => {
    await copyLoginUrlToClipboard()
  }

  const handleShowAuthInfo = () => {
    showAuthInfo()
  }

  const handleGoToAccessPage = () => {
    history.push(path(location, 'access'))
  }

  const handleTestSpecificUrl = () => {
    testLoginUrl('https://arwebit.8thwall.app/iscag-ei/login/example')
  }

  // Funzioni per la gestione degli URL condivisibili
  const handleGenerateExperienceUrl = (experienceId: string, params: Record<string, string> = {}) => {
    const url = generateShareableExperienceUrl(experienceId, params, false)
    console.log(`🔗 URL esperienza ${experienceId}:`, url)
    return url
  }

  const handleGenerateExperienceLoginUrl = (experienceId: string, params: Record<string, string> = {}) => {
    const url = generateExperienceLoginUrl(experienceId, params)
    console.log(`🔐 URL login con reindirizzamento a ${experienceId}:`, url)
    return url
  }

  const handleCopyExperienceUrl = async (experienceId: string, params: Record<string, string> = {}) => {
    const url = generateShareableExperienceUrl(experienceId, params, false)
    try {
      await navigator.clipboard.writeText(url)
      console.log('📋 URL copiato negli appunti:', url)
    } catch (error) {
      console.error('Errore nella copia negli appunti:', error)
    }
  }

  const handleCopyExperienceLoginUrl = async (experienceId: string, params: Record<string, string> = {}) => {
    const url = generateExperienceLoginUrl(experienceId, params)
    try {
      await navigator.clipboard.writeText(url)
      console.log('📋 URL di login copiato negli appunti:', url)
    } catch (error) {
      console.error('Errore nella copia negli appunti:', error)
    }
  }

  const handleLogout = () => {
    authSystem.logout()
    const loginUrl = path(location, authSystem.getCurrentLoginUrl())
    console.log('🚪 Logout - Reindirizzamento a:', loginUrl)
    history.push(loginUrl)
  }

  const formatExpiryTime = (timestamp: number): string => {
    const date = new Date(timestamp)
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const authStatus = authSystem.getAuthStatus()

  return (
    <Page title='Immersive Web Museo Genio' top={true}>

      {/* Loading e Errori */}
    

      {error && (
        <div style={{textAlign: 'center', padding: '20px', color: '#d32f2f', backgroundColor: '#ffebee', borderRadius: '8px', marginBottom: '20px'}}>
          <div style={{fontSize: '20px', marginBottom: '10px'}}>⚠️</div>
          <div>{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{marginTop: '10px', padding: '8px 16px', backgroundColor: '#d32f2f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}
          >
            Riprova
          </button>
        </div>
      )}
    
      {/* Informazioni di autenticazione */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '10px',
        padding: '15px',
        marginBottom: '20px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: '20px',
      }}>
        <div>
          <strong>🔐 Sessione attiva</strong>
          <br />
          <small>Token valido fino: {authStatus.tokenExpiresAt ? formatExpiryTime(authStatus.tokenExpiresAt) : 'Non disponibile'}</small>
        </div>
        <button
          onClick={handleLogout}
          style={{
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '5px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '0.9em'
          }}
        >
          🚪 Logout
        </button>
      </div>
      
      {/* Pulsanti di debug e test */}
      <div style={{ marginBottom: '20px', textAlign: 'center', display: 'none' }}>
        <button 
          onClick={handleTestModels}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#4CAF50', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Modelli
        </button>
        <button 
          onClick={handleDiscoverModels}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#2196F3', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Scopri Modelli
        </button>
        <button 
          onClick={handleShowModelsInfo}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#FF9800', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Info Modelli
        </button>
        <button 
          onClick={handleInitializeRemoteModels}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#9C27B0', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Inizializza Remoti
        </button>
        <button 
          onClick={handleShowModelsByCategory}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#607D8B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Per Categoria
        </button>
        
        {/* Pulsanti per gestione autenticazione */}
        <button 
          onClick={handleShowLoginUrl}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#E91E63', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Mostra URL Login
        </button>
        <button 
          onClick={handleCopyLoginUrl}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#FF5722', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Copia URL Login
        </button>
        <button 
          onClick={handleShowAuthInfo}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#795548', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Info Auth
        </button>
        <button 
          onClick={handleGoToAccessPage}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#009688', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          📱 Pagina QR Code
        </button>
        <button 
          onClick={handleTestSpecificUrl}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#FF6B6B', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🧪 Test URL Specifico
        </button>
        <button 
          onClick={handleTestWordPressConnection}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#FFC107', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🌐 Test WordPress
        </button>
        <button 
          onClick={handleClearWordPressCache}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#F44336', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🧹 Pulisci Cache
        </button>
        <button 
          onClick={handleWordPressDiagnostics}
          style={{ 
            margin: '5px', 
            padding: '8px 16px', 
            backgroundColor: '#9C27B0', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔍 Diagnostica WordPress
        </button>
      </div>
      
      {/* Indicatore stato WordPress */}
      <div style={{
        background: wordpressStatus.isOnline ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)' : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
        borderRadius: '15px',
        padding: '15px 20px',
        marginBottom: '20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        color: 'white',
        display: 'none',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>
            {wordpressStatus.isOnline ? '✅' : '❌'}
          </span>
          <span style={{ fontWeight: 'bold' }}>
            {wordpressStatus.isOnline ? 'WordPress Online' : 'WordPress Offline'}
          </span>
          {wordpressStatus.lastCheck && (
            <span style={{ fontSize: '12px', opacity: 0.8 }}>
              (Ultimo controllo: {wordpressStatus.lastCheck.toLocaleTimeString()})
            </span>
          )}
        </div>
        {wordpressStatus.isOnline && (
          <div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.9 }}>
            Caricate {experiences.length} esperienze dal database WordPress
          </div>
        )}
        {!wordpressStatus.isOnline && (
          <div style={{ marginTop: '10px', fontSize: '14px', opacity: 0.9 }}>
            Utilizzando esperienze di fallback ({experiences.length} disponibili)
          </div>
        )}
      </div>

  {/* Filtro di ricerca */}
  <div style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        borderRadius: '15px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flex: '1', maxWidth: '500px' }}>
            <input
              type="text"
              placeholder="Cerca esperienze..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '15px 20px',
                paddingLeft: '50px',
                fontSize: '16px',
                border: 'none',
                borderRadius: '25px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                outline: 'none'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '18px',
              color: '#666'
            }}>
              🔍
            </span>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '25px',
                padding: '15px 20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ✕ Cancella
            </button>
          )}
        </div>
        {searchTerm && (
          <div style={{
            textAlign: 'center',
            marginTop: '15px',
            color: 'white',
            fontSize: '14px'
          }}>
            Trovati {filteredExperiences.length} risultati per "{searchTerm}"
          </div>
        )}
      </div>

      {loading && (
        <div style={{textAlign: 'center', padding: '40px'}}>
          <div style={{fontSize: '24px', marginBottom: '10px'}}>⏳</div>
          <div>Caricamento esperienze...</div>
        </div>
      )}
      
      <OneColumnGrid>
        {filteredExperiences
          .filter(exp => {
            // Supporta sia il formato "wrapped" (WordPress/AR: {id, img, config})
            // sia eventuali config "raw" (fallback: experienceConfigs)
            const cfg = (exp && (exp as any).config) ? (exp as any).config : exp
            const experienceType = cfg?.experienceType
            const modelType = cfg?.modelType

            // Escludi sempre i video dal menu
            if (experienceType === 'video') return false

            // Mostra solo:
            // - tutte le esperienze 3D
            // - le esperienze AR basate su template genericAR
            // - il personaggio animato (template tutaAmianto)
            return (
              experienceType === '3d' ||
              modelType === 'genericAR' ||
              modelType === 'tutaAmianto'
            )
          })
          .map(exp => (
          <NavCard
            key={exp.id}
            title={exp.config.description }
            to={path(location, '3d-experience')}
            text= ""
            img={exp.img}
            onClick={() => handleModelClick(exp.config)}
          />
        ))}
      </OneColumnGrid>

    
    </Page>
  )
})

export {Home}

